/**
 * In-Presentation Search Agent Parameter Schema
 * 
 * IMPORTANT: All array and boolean types must have explicit descriptions
 * to help the LLM generate correct JSON types (not stringified values).
 */

import { z } from "zod";
import { SubAgentParams } from "../../../core/types";

export const InPresentationSearchParamsSchema = z.object({
    // Base params - explicit descriptions for proper JSON generation
    requirements: z.array(z.object({
        id: z.string().describe("Requirement identifier like 'A', 'B', 'C'"),
        requirement: z.string().describe("The specific search requirement text"),
    })).describe("Array of search requirements to fulfill. Must be a JSON array, not a string."),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    
    context: z.array(z.object({
        context: z.array(z.string()).describe("Array of context IDs like 'd0|slide_257', 'component_abc123'"),
        purpose: z.string().describe("Description of what this context is for"),
    })).optional().describe("Optional array of context items with context IDs"),
    
    threadId: z.string().optional().describe("Optional thread ID for conversation continuation"),
    
    // In-Presentation Search specific
    documentId: z.union([
        z.string(),
        z.number(),
    ]).optional()
        .describe("Single deck selector. Use numeric index (0, 1, 2), 'CURRENT' for active deck, explicit deck ID, or 'ALL' for global backend indexed library. Defaults to 'CURRENT'."),
    
    documentIds: z.array(z.union([
        z.string(),
        z.number(),
    ])).optional()
        .describe("Multiple decks to search across. Array of indexes (0, 1, 2), 'CURRENT', or explicit deck IDs."),
}).superRefine((params, ctx) => {
    if (params.documentId !== undefined && params.documentIds !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['documentIds'],
            message: "Provide either documentId or documentIds, not both.",
        });
    }
});

// Extend SubAgentParams to ensure type compatibility
export interface InPresentationSearchParams extends SubAgentParams {
    /** Deck to search in - index, 'CURRENT', or explicit deck ID */
    documentId?: string | number;
    /** Multiple decks to search across */
    documentIds?: (string | number)[];
}




